// utils/currencyExchange.js

const EXCHANGE_API_URL = 'https://api.frankfurter.app';
const RATE_ADJUSTMENT = parseFloat(process.env.REACT_APP_EXCHANGE_RATE_ADJUSTMENT || '0');

let ratesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 24 * 60 * 60 * 1000;

export async function getExchangeRates(baseCurrency = 'USD') {
  if (ratesCache && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
    if (ratesCache.base_code === baseCurrency) {
      return ratesCache;
    }
  }

  try {
    const response = await fetch(`${EXCHANGE_API_URL}/latest?from=${baseCurrency}`);
    const data = await response.json();

    if (data && data.rates) {
      const adjustedRates = {};
      for (const [currency, rate] of Object.entries(data.rates)) {
        adjustedRates[currency] = rate * (1 + RATE_ADJUSTMENT / 100);
      }

      adjustedRates[baseCurrency] = 1;

      ratesCache = {
        base_code: baseCurrency,
        conversion_rates: adjustedRates,
        time_last_update: data.date || new Date().toISOString().split('T')[0]
      };
      cacheTimestamp = Date.now();

      console.log('✅ Exchange rates fetched successfully:', ratesCache);
      return ratesCache;
    } else {
      throw new Error('Invalid API response');
    }
  } catch (error) {
    console.error('Exchange rate fetch error:', error);
    return getFallbackRates(baseCurrency);
  }
}

export function convertCurrency(amount, fromCurrency, toCurrency, rates) {
  if (fromCurrency === toCurrency) return amount;
  
  if (!rates || !rates.conversion_rates) {
    console.warn('No exchange rates available');
    return amount;
  }

  if (rates.base_code === fromCurrency) {
    return amount * (rates.conversion_rates[toCurrency] || 1);
  }
  
  const toBase = 1 / (rates.conversion_rates[fromCurrency] || 1);
  const toTarget = rates.conversion_rates[toCurrency] || 1;
  
  return amount * toBase * toTarget;
}

export function groupCampaignsByCurrency(campaigns) {
  const groups = {};
  
  campaigns.forEach(campaign => {
    const currency = campaign.currency || 'USD';
    if (!groups[currency]) {
      groups[currency] = {
        campaigns: [],
        totalRevenue: 0,
        totalClicks: 0,
        validClicks: 0
      };
    }
    
    groups[currency].campaigns.push(campaign);
    groups[currency].totalRevenue += campaign.today_revenue || 0;
    groups[currency].totalClicks += campaign.today_clicks || 0;
  });
  
  return groups;
}

function getFallbackRates(baseCurrency) {
  console.warn('⚠️ Using fallback exchange rates');
  
  const fallbackRates = {
    'USD': { 'USD': 1, 'EUR': 0.92, 'GBP': 0.79, 'CAD': 1.36, 'AUD': 1.52, 'JPY': 149.50, 'CNY': 7.24, 'INR': 83.12 },
    'EUR': { 'EUR': 1, 'USD': 1.09, 'GBP': 0.86, 'CAD': 1.48, 'AUD': 1.65, 'JPY': 162.50, 'CNY': 7.88, 'INR': 90.40 },
    'GBP': { 'GBP': 1, 'USD': 1.27, 'EUR': 1.16, 'CAD': 1.72, 'AUD': 1.92, 'JPY': 189.20, 'CNY': 9.18, 'INR': 105.30 }
  };

  return {
    base_code: baseCurrency,
    conversion_rates: fallbackRates[baseCurrency] || fallbackRates['USD'],
    time_last_update: 'Fallback rates (static)'
  };
}